import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  registerServiceWorker
} from '../services/pushNotifications';

const NotificationPermissionModal = ({ user, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if we should show the modal
    const checkPermission = async () => {
      if (!isNotificationSupported()) return;
      
      const permission = getNotificationPermission();
      const hasAsked = localStorage.getItem('notification_permission_asked');
      
      // Show modal if permission is default and we haven't asked before
      if (permission === 'default' && !hasAsked && user) {
        // Delay showing to not interrupt initial page load
        setTimeout(() => setIsVisible(true), 2000);
      }
    };
    
    checkPermission();
  }, [user]);

  const handleAllow = useCallback(async () => {
    setIsLoading(true);
    localStorage.setItem('notification_permission_asked', 'true');
    
    try {
      // Register service worker first
      await registerServiceWorker();
      
      // Request permission
      const result = await requestNotificationPermission();
      
      if (result.granted && user) {
        // Subscribe to push notifications
        await subscribeToPush(user.id, user.user_type);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    }
    
    setIsLoading(false);
    setIsVisible(false);
    if (onClose) onClose();
  }, [user, onClose]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem('notification_permission_asked', 'true');
    setIsVisible(false);
    if (onClose) onClose();
  }, [onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-blue-600" />
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Resta aggiornato
          </h2>
          
          <p className="text-gray-600 mb-6">
            Ricevi notifiche quando qualcuno si candida ai tuoi annunci o ti invia un messaggio
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDismiss}
              disabled={isLoading}
            >
              Non ora
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={handleAllow}
              disabled={isLoading}
            >
              {isLoading ? 'Attivazione...' : 'Consenti'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionModal;
